import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Building2, RefreshCw, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Vendors() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [newVendor, setNewVendor] = useState({
    company_name: '', contact_person: '', email: '', phone: '',
    gst_number: '', pan_number: '', address: '', city: '', state: '', pincode: '',
    payment_terms: 30, notes: ''
  });

  useEffect(() => { fetchVendors(); }, [token]);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/purchases/vendors`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setVendors((await res.json()).vendors || []);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const createVendor = async () => {
    if (!newVendor.company_name) { toast.error('Company name required'); return; }
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/purchases/vendors`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newVendor)
      });
      if (res.ok) {
        toast.success('Vendor created');
        setShowNew(false);
        setNewVendor({ company_name: '', contact_person: '', email: '', phone: '', gst_number: '', pan_number: '', address: '', city: '', state: '', pincode: '', payment_terms: 30, notes: '' });
        fetchVendors();
      } else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (error) { toast.error('Failed'); }
    finally { setProcessing(false); }
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amt || 0);

  const filtered = vendors.filter(v => !search || v.company_name?.toLowerCase().includes(search.toLowerCase()) || v.vendor_code?.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-6 w-6 animate-spin" /></div>;

  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2"><Building2 className="h-5 w-5" />Vendors</CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-48" />
            </div>
            <Dialog open={showNew} onOpenChange={setShowNew}>
              <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />Add Vendor</Button></DialogTrigger>
              <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Add Vendor</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div><Label>Company Name *</Label><Input value={newVendor.company_name} onChange={(e) => setNewVendor({ ...newVendor, company_name: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Contact Person</Label><Input value={newVendor.contact_person} onChange={(e) => setNewVendor({ ...newVendor, contact_person: e.target.value })} /></div>
                    <div><Label>Phone</Label><Input value={newVendor.phone} onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })} /></div>
                  </div>
                  <div><Label>Email</Label><Input type="email" value={newVendor.email} onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>GSTIN</Label><Input value={newVendor.gst_number} onChange={(e) => setNewVendor({ ...newVendor, gst_number: e.target.value })} /></div>
                    <div><Label>PAN</Label><Input value={newVendor.pan_number} onChange={(e) => setNewVendor({ ...newVendor, pan_number: e.target.value })} /></div>
                  </div>
                  <div><Label>Address</Label><Textarea value={newVendor.address} onChange={(e) => setNewVendor({ ...newVendor, address: e.target.value })} rows={2} /></div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>City</Label><Input value={newVendor.city} onChange={(e) => setNewVendor({ ...newVendor, city: e.target.value })} /></div>
                    <div><Label>State</Label><Input value={newVendor.state} onChange={(e) => setNewVendor({ ...newVendor, state: e.target.value })} /></div>
                    <div><Label>Pincode</Label><Input value={newVendor.pincode} onChange={(e) => setNewVendor({ ...newVendor, pincode: e.target.value })} /></div>
                  </div>
                  <div><Label>Payment Terms (days)</Label><Input type="number" min="0" value={newVendor.payment_terms} onChange={(e) => setNewVendor({ ...newVendor, payment_terms: parseInt(e.target.value) || 30 })} /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
                  <Button onClick={createVendor} disabled={processing}>{processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Company Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>GSTIN</TableHead>
              <TableHead className="text-right">Balance Due</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((v) => (
              <TableRow key={v.vendor_id}>
                <TableCell className="font-mono text-sm">{v.vendor_code}</TableCell>
                <TableCell className="font-medium">{v.company_name}</TableCell>
                <TableCell><div className="text-sm"><p>{v.contact_person}</p><p className="text-slate-500">{v.phone}</p></div></TableCell>
                <TableCell className="text-xs">{v.gst_number || '-'}</TableCell>
                <TableCell className="text-right">{v.balance_due > 0 ? <span className="text-red-600 font-medium">{formatCurrency(v.balance_due)}</span> : formatCurrency(0)}</TableCell>
                <TableCell><Badge variant={v.status === 'active' ? 'default' : 'secondary'}>{v.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filtered.length === 0 && <div className="text-center py-8 text-slate-500">No vendors found</div>}
      </CardContent>
    </Card>
  );
}
